import { Permission } from '@act/shared/authorization';
import {
  MemberTransferReasonDto,
  MemberTransferRequestDto,
  MemberTransferRequestFormSchema,
  MemberTransferRequestFormType,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Button, FormBuilder, SelectOption } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Card,
  CardActions,
  CardContent,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { TrackingProvider } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { memberTransferRequests } from 'redux/requests/member-transfer.request';
import { selectActingUser } from 'redux/slices/actingUserSlice';
import {
  createMemberTransferRequestSuccess,
  getMemberTransferReasons,
  memberTransferRequestSelectors,
} from 'redux/slices/memberTransferRequestSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import styled from 'styled-components';
import { reemaColors } from 'toolkit/styles/colors';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import { useToast } from '../app/ReusableToast';
import Loader from '../workflows/loader';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';
import { DATE_FORMAT } from './scheduled-event-dialog';
import { createTransferRequestFormConfig } from './transfer-request-form-config';

const CardTitleDiv = styled.div`
  margin-bottom: 8px;
`;

const CardInformationDiv = styled.div`
  max-width: 525px;
`;

const CardFormSectionDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 40px 0;
`;

export interface TransferRequestUniqueProps {
  requestMember: UserDto;
  transferRequest?: MemberTransferRequestDto;
}

export type TransferRequestDialogProps = GenericDialogProps<boolean> &
  TransferRequestUniqueProps;

export type TransferRequestDialogCallbackInput = unknown;

export const TransferRequestDialog = ({
  requestMember,
  transferRequest,
  open,
  setOpen,
}: TransferRequestDialogProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  const memberTransferReasons =
    memberTransferRequestSelectors.useSelectMemberTransferReasons();
  const memberTransferLoadingStatus =
    memberTransferRequestSelectors.useSelectMemberTransferSliceStatuses();
  const actingUser = useAppSelector(selectActingUser);

  const { control, getValues, setValue, watch, formState, handleSubmit } =
    useForm<MemberTransferRequestFormType>({
      defaultValues: {
        memberId: requestMember?.id,
        clientId: requestMember?.clientId,
        originatingGuideId: requestMember?.assignedGuideId,
        receivingGuideId: '',
        creatorId: actingUser?.id,
        transferReasonId: null,
        commentToReceivingGuide: '',
        stringDetail: '',
        numericDetail: null,
        timestampDetail: transferRequest?.timestampDetail
          ? DateTime.fromMillis(transferRequest.timestampDetail).toFormat(
              DATE_FORMAT
            )
          : DateTime.now().toFormat(DATE_FORMAT),
      },
      resolver: zodResolver(MemberTransferRequestFormSchema),
    });

  const watchTransferReasonId = parseInt(watch('transferReasonId')?.toString());
  const watchReceivingGuideId = watch('receivingGuideId');
  const [selectedTransferReason, setSelectedTransferReason] =
    useState<MemberTransferReasonDto>(null);

  const canAuditTransferRequest = membersAppSelectors.useHasPermissionOnMember(
    requestMember.id,
    Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS
  );

  const canAuditTransferRequestWithoutGuide =
    membersAppSelectors.useHasPermissionOnMember(
      requestMember.id,
      Permission.CAN_AUDIT_MEMBER_TRANSFER_REQUESTS_WITHOUT_GUIDE
    );

  // For members without an assigned guide, check the _WITHOUT_GUIDE permission
  const canAudit =
    canAuditTransferRequest ||
    (!requestMember?.assignedGuideId && canAuditTransferRequestWithoutGuide);

  const [
    createMemberTransferRequestResult,
    _createMemberTransferRequestLoadingStatus,
    createMemberTransferRequest,
  ] = useApiCall(memberTransferRequests.createMemberTransferRequest);
  useEffect(() => {
    const newTransferReason = memberTransferReasons.find(
      r => r.id === watchTransferReasonId
    );
    if (newTransferReason === selectedTransferReason) return;

    setSelectedTransferReason(newTransferReason);
  }, [
    memberTransferReasons,
    selectedTransferReason,
    setValue,
    transferRequest,
    watchReceivingGuideId,
    watchTransferReasonId,
  ]);

  const [addressOptions, setAddressOptions] =
    useState<SelectOption<number>[]>();

  useEffect(() => {
    if (requestMember) {
      const addressOptions: SelectOption<number>[] =
        requestMember?.addresses?.map(a => {
          const label = `${a.line1}, ${a.line2 ? a.line2 + ', ' : ''}${
            a.city
          }, ${a.state} ${a.zip}`;
          return { label, value: a.id };
        });
      setAddressOptions(addressOptions);
    }
  }, [requestMember, setValue]);

  useEffect(() => {
    async function dispatchGetMemberTransferReasons() {
      await dispatch(await getMemberTransferReasons({ params: null }));
    }

    if (memberTransferLoadingStatus.reasonsStatus === 'initial') {
      void dispatchGetMemberTransferReasons();
    }
  }, [dispatch, memberTransferLoadingStatus.reasonsStatus]);

  useEffect(() => {
    if (createMemberTransferRequestResult) {
      dispatch(
        createMemberTransferRequestSuccess(createMemberTransferRequestResult)
      );
      toast('Transfer request sent successfully');
      setOpen(false);
    }
  }, [createMemberTransferRequestResult, dispatch]);

  const { isDirty, isValid } = formState;

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  async function onSubmit(data: MemberTransferRequestFormType) {
    const values = getValues();

    const transferDate = values?.timestampDetail
      ? DateTime.fromFormat(values?.timestampDetail, DATE_FORMAT)
          .startOf('day')
          .toMillis()
      : DateTime.local().startOf('day').toMillis();

    createMemberTransferRequest({
      body: {
        memberId: data.memberId,
        receivingGuideId: data.receivingGuideId,
        transferReasonId: data.transferReasonId,
        stringDetail: data.stringDetail,
        numericDetail: data.numericDetail,
        commentToReceivingGuide: data.commentToReceivingGuide,
        originatingGuideId: requestMember?.assignedGuideId ?? null,
        timestampDetail: transferDate,
        ...(canAudit && {
          approval: 'approve',
          effectiveAt:
            transferDate || DateTime.local().startOf('day').toMillis(),
        }),
      },
    });
  }

  const formConfig = createTransferRequestFormConfig({
    memberTransferReasons,
    selectedTransferReason,
    addressOptions,
    control,
    errors: formState.errors,
  });

  return (
    <TrackingProvider contextName="transfer-request-dialog">
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        hideBackdrop
        sx={{
          marginTop: '51px',
          '& MuiModal-backdrop': {
            marginTop: '51px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '44px',
            padding: '18px 36px',
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 1px 3px 1px rgba(0, 0, 0, 0.08)',
            zIndex: '2',
          }}
        >
          <IconButton onClick={handleClose}>
            <ArrowBackIcon />
          </IconButton>{' '}
          Member Transfer Request
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'grid',
            justifyContent: 'center',
            paddingTop: '12px !important',
            backgroundColor: reemaColors.lightBackground,
            zIndex: '1',
          }}
        >
          {memberTransferLoadingStatus.reasonsStatus !== 'success' && (
            <Loader />
          )}
          {memberTransferLoadingStatus.reasonsStatus === 'success' && (
            <Card
              sx={{
                height: 'min-content',
                maxWidth: '570px',
              }}
            >
              <CardContent
                sx={{
                  padding: '24px 24px 0',
                  '&:last-child': {
                    paddingBottom: 0,
                  },
                }}
              >
                <CardTitleDiv>
                  <Typography variant="titleLarge">
                    We need a little info about your request.
                  </Typography>
                </CardTitleDiv>
                <CardInformationDiv>
                  <Typography variant="bodyLarge">
                    Providing these details will help us resolve the request. It
                    may take a few days for a change to be made.
                  </Typography>
                </CardInformationDiv>
                <CardFormSectionDiv>
                  <FormBuilder
                    config={formConfig}
                    control={control}
                    formState={formState}
                  />
                </CardFormSectionDiv>
              </CardContent>
              <Divider sx={{ marginTop: '48px' }} />
              <CardActions
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'right',
                  padding: '8px 24px',
                }}
              >
                <Button onClick={handleClose} trackingName="cancel-button">
                  Cancel
                </Button>
                <Button
                  disabled={!isValid}
                  debounced
                  onClick={handleSubmit(onSubmit)}
                  type={'submit'}
                  variant="contained"
                  trackingName="send-transfer-request-button"
                >
                  Send request
                </Button>
              </CardActions>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </TrackingProvider>
  );
};
